import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TutorialContainer } from '@/features/tutorial/TutorialContainer';
import { NonTechnicalLevelContainer } from '@/features/tutorial/NonTechnicalLevelContainer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { StarRating } from '@/components/ui/Progress';
import { useUserStore } from '@/stores/userStore';
// Technical track levels
import level1Data from '@/data/levels/level-1.json';
import level2Data from '@/data/levels/level-2.json';
import level3Data from '@/data/levels/level-3.json';
import level4Data from '@/data/levels/level-4.json';
import level5Data from '@/data/levels/level-5.json';

// Non-technical track levels
import ntLevel1Data from '@/data/levels/nt-level-1.json';
import ntLevel2Data from '@/data/levels/nt-level-2.json';
import ntLevel3Data from '@/data/levels/nt-level-3.json';
import ntLevel4Data from '@/data/levels/nt-level-4.json';
import ntLevel5Data from '@/data/levels/nt-level-5.json';

export default function TutorialPage() {
  const { levelId } = useParams();
  const navigate = useNavigate();
  const [levelData, setLevelData] = useState<any>(null);
  const [selectedTrack, setSelectedTrack] = useState<'technical' | 'non-technical' | null>(null);
  const { progress } = useUserStore();

  // Technical track levels
  const technicalLevels = [
    {
      id: 'level-1',
      number: 1,
      title: 'Getting Started',
      difficulty: 'Beginner',
      description: 'Learn Read, Glob, and Grep tools',
    },
    {
      id: 'level-2',
      number: 2,
      title: 'Reading Files',
      difficulty: 'Beginner',
      description: 'Master file navigation and reading',
    },
    {
      id: 'level-3',
      number: 3,
      title: 'Making Changes',
      difficulty: 'Intermediate',
      description: 'Learn to edit and modify code',
    },
    {
      id: 'level-4',
      number: 4,
      title: 'Git Workflows',
      difficulty: 'Intermediate',
      description: 'Work with version control',
    },
    {
      id: 'level-5',
      number: 5,
      title: 'Advanced Features',
      difficulty: 'Advanced',
      description: 'Master complex operations',
    },
  ];

  // Non-technical track levels
  const nonTechnicalLevels = [
    {
      id: 'nt-level-1',
      number: 1,
      title: 'First Conversation',
      difficulty: 'Beginner',
      description: 'Chat with Claude AI',
    },
    {
      id: 'nt-level-2',
      number: 2,
      title: 'Document Analysis',
      difficulty: 'Beginner',
      description: 'Upload and analyze documents',
    },
    {
      id: 'nt-level-3',
      number: 3,
      title: 'Spreadsheet Data',
      difficulty: 'Intermediate',
      description: 'Work with data and spreadsheets',
    },
    {
      id: 'nt-level-4',
      number: 4,
      title: 'Image Analysis',
      difficulty: 'Intermediate',
      description: 'Analyze visual documents',
    },
    {
      id: 'nt-level-5',
      number: 5,
      title: 'Multi-Document Analysis',
      difficulty: 'Advanced',
      description: 'Master advanced techniques',
    },
  ];

  const levels = selectedTrack === 'non-technical' ? nonTechnicalLevels : technicalLevels;

  // Debug: Log progress when track is selected
  useEffect(() => {
    if (selectedTrack && !levelId) {
      console.log('📊 Current tutorial progress:', progress.tutorialProgress);
      console.log('🎯 Selected track:', selectedTrack);
    }
  }, [selectedTrack, levelId, progress]);

  // Determine which levels are unlocked
  const getLevelStatus = (levelNumber: number) => {
    // Level 1 is always unlocked
    if (levelNumber === 1) {
      // Check if level 1 is completed (for either track)
      const prefix = selectedTrack === 'non-technical' ? 'nt-level-' : 'level-';
      const currentLevelId = `${prefix}${levelNumber}`;
      const currentProgress = progress.tutorialProgress.find(
        (p) => p.levelId === currentLevelId
      );
      return {
        unlocked: true,
        completed: currentProgress?.completed || false,
        stars: currentProgress?.stars || 0
      };
    }

    // Build level IDs based on selected track
    const prefix = selectedTrack === 'non-technical' ? 'nt-level-' : 'level-';
    const previousLevelId = `${prefix}${levelNumber - 1}`;
    const currentLevelId = `${prefix}${levelNumber}`;

    // Check if previous level is completed
    const previousProgress = progress.tutorialProgress.find(
      (p) => p.levelId === previousLevelId
    );

    const currentProgress = progress.tutorialProgress.find(
      (p) => p.levelId === currentLevelId
    );

    return {
      unlocked: previousProgress?.completed || false,
      completed: currentProgress?.completed || false,
      stars: currentProgress?.stars || 0,
    };
  };

  useEffect(() => {
    // Load level data dynamically
    const loadLevelData = () => {
      switch (levelId) {
        // Technical track
        case 'level-1':
        case '1':
          setLevelData(level1Data);
          setSelectedTrack('technical');
          break;
        case 'level-2':
        case '2':
          setLevelData(level2Data);
          setSelectedTrack('technical');
          break;
        case 'level-3':
        case '3':
          setLevelData(level3Data);
          setSelectedTrack('technical');
          break;
        case 'level-4':
        case '4':
          setLevelData(level4Data);
          setSelectedTrack('technical');
          break;
        case 'level-5':
        case '5':
          setLevelData(level5Data);
          setSelectedTrack('technical');
          break;
        // Non-technical track
        case 'nt-level-1':
          setLevelData(ntLevel1Data);
          setSelectedTrack('non-technical');
          break;
        case 'nt-level-2':
          setLevelData(ntLevel2Data);
          setSelectedTrack('non-technical');
          break;
        case 'nt-level-3':
          setLevelData(ntLevel3Data);
          setSelectedTrack('non-technical');
          break;
        case 'nt-level-4':
          setLevelData(ntLevel4Data);
          setSelectedTrack('non-technical');
          break;
        case 'nt-level-5':
          setLevelData(ntLevel5Data);
          setSelectedTrack('non-technical');
          break;
        default:
          setLevelData(null);
      }
    };

    if (levelId) {
      loadLevelData();
    }
  }, [levelId]);

  // Level selection screen
  if (!levelId) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-4"
            >
              ← Back to Home
            </Button>
            <h1 className="text-4xl font-bold mb-2 text-primary-400">
              Tutorial Mode
            </h1>
            <p className="text-primary-200">
              Master Claude Code through guided lessons
            </p>
          </div>

          {/* Track Selection */}
          {!selectedTrack && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Card
                variant="elevated"
                className="hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105"
                onClick={() => setSelectedTrack('technical')}
              >
                <CardHeader>
                  <div className="text-4xl mb-3 text-center">💻</div>
                  <CardTitle className="text-2xl text-center">Technical Track</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-primary-200 mb-4 text-center">
                    For developers and programmers
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Learn Read, Glob, Grep, and Edit tools</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Navigate codebases efficiently</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Make code changes with AI assistance</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Master Git workflows</span>
                    </div>
                  </div>
                  <Badge variant="info" size="sm" className="w-full justify-center">
                    5 Levels • Beginner to Advanced
                  </Badge>
                </CardContent>
              </Card>

              <Card
                variant="elevated"
                className="hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105"
                onClick={() => setSelectedTrack('non-technical')}
              >
                <CardHeader>
                  <div className="text-4xl mb-3 text-center">📄</div>
                  <CardTitle className="text-2xl text-center">Non-Technical Track</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-primary-200 mb-4 text-center">
                    For everyone - no coding required
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Chat with Claude AI naturally</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Analyze documents and spreadsheets</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Extract insights from images</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-primary-300">
                      <span>✓</span>
                      <span>Master multi-document analysis</span>
                    </div>
                  </div>
                  <Badge variant="success" size="sm" className="w-full justify-center">
                    5 Levels • Beginner to Advanced
                  </Badge>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Level Grid */}
          {selectedTrack && (
            <>
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTrack(null)}
                  >
                    ← Change Track
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">
                      {selectedTrack === 'technical' ? '💻' : '📄'}
                    </span>
                    <h2 className="text-xl font-bold text-primary-400">
                      {selectedTrack === 'technical' ? 'Technical Track' : 'Non-Technical Track'}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {levels.map((level) => {
                  const status = getLevelStatus(level.number);
                  return (
                    <Card
                      key={level.id}
                      variant={status.unlocked ? 'elevated' : 'default'}
                      className={
                        status.unlocked
                          ? 'hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105'
                          : 'opacity-50 cursor-not-allowed'
                      }
                      onClick={() =>
                        status.unlocked && navigate(`/tutorial/${level.id}`)
                      }
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-primary-500">
                              {level.number}
                            </span>
                            {!status.unlocked && <span className="text-xl">🔒</span>}
                            {status.completed && <span className="text-xl">✓</span>}
                          </div>
                          <Badge
                            variant={
                              level.difficulty === 'Beginner'
                                ? 'success'
                                : level.difficulty === 'Intermediate'
                                ? 'warning'
                                : 'error'
                            }
                            size="sm"
                          >
                            {level.difficulty}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg">{level.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-primary-300 mb-3">
                          {level.description}
                        </p>
                        {status.completed && (
                          <div className="mb-3">
                            <StarRating stars={status.stars as 0 | 1 | 2 | 3} size="sm" />
                          </div>
                        )}
                        {status.unlocked ? (
                          <Button variant="primary" size="sm" className="w-full">
                            {status.completed ? 'Play Again' : 'Start Level'}
                          </Button>
                        ) : (
                          <p className="text-sm text-primary-200/50">
                            Complete previous levels to unlock
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (!levelData) {
    return (
      <div className="min-h-screen p-8 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-primary-200">Loading level...</p>
        </div>
      </div>
    );
  }

  // Level content
  const isNonTechnical = levelData?.track === 'non-technical' || levelId?.startsWith('nt-');

  return (
    <div className="min-h-screen p-8 bg-black">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tutorial')}
        >
          ← Back to Levels
        </Button>
      </div>
      {isNonTechnical ? (
        <NonTechnicalLevelContainer levelData={levelData} />
      ) : (
        <TutorialContainer levelData={levelData} />
      )}
    </div>
  );
}
